import React from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Trophy,
  Users,
  BarChart3,
  Info,
  Wifi,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Store,
  Cog,
  EyeOff,
  Archive,
} from "lucide-react"
import { TournamentStatusBadge } from "./ui/TournamentStatusBadge"
import { useTournamentRounds } from "@/graphql/hooks/useTournamentRounds"
import { useAuthContext } from "@/contexts/AuthContext"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useRoundStart } from "@/graphql/hooks/useRoundStart"
import { useTournamentFinish } from "@/graphql/hooks/useTournamentFinish"
import { useNavigate } from "react-router-dom"
import { Tournament, useTournamentOrganizers } from "@/graphql"

interface TournamentNavigationProps {
  tournament: Tournament
  tournamentName: string
  tournamentStatusId?: number
  currentPage?: "detail" | "players" | "rounds" | "report" | "admin"
  showRounds?: boolean
  forceFetchRounds?: boolean
}

const roundStatus = {
  1: "En curso",
  2: "Terminada",
  3: "Anulada",
}

export const TournamentNavigation: React.FC<TournamentNavigationProps> = ({
  tournament,
  tournamentName,
  tournamentStatusId,
  currentPage,
  showRounds = false,
}) => {
  const { userInfo, isAdmin, isAuthenticated } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()

  const pathStanding = location.pathname.includes("tournament/standing/")
  const pathRounds = location.pathname.includes("tournament/round/")

  // Busca los organizadores
  const { organizers } = useTournamentOrganizers(tournament?.id, isAuthenticated)

  // Verificamos los permisos.
  const isStoreOwner = tournament && userInfo?.User?.Person?.Store?.id === tournament.storeId

  const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)
  const canManageTournament = isAdmin || isStoreOwner || isOrganizer

  // para finalizar el torneo
  const { roundStart, loading: startLoading } = useRoundStart()
  // para reportar el torneo
  const { tournamentFinish, loading: finishLoading } = useTournamentFinish()

  // Rondas.
  const { rounds } = useTournamentRounds(
    tournament?.id,
    showRounds ? canManageTournament : showRounds,
  )

  const { roundId } = useParams()

  const stateRounds = [3, 4, 5, 6].includes(tournamentStatusId)

  const allMatchesReported =
    rounds?.some((round) => {
      return round.statusId === 1 && Number(round.totalMatchPendings) === 0
    }) || false

  const showEndTournament = tournamentStatusId === 4 && canManageTournament && allMatchesReported
  const showReportTournament = tournamentStatusId === 5 && canManageTournament

  const handleEndTournament = async () => {
    try {
      const resolve = await roundStart({
        tournamentId: tournament.id,
        addNew: false,
        earlyClose: false,
      })
      window.open(resolve.Round.urlStanding + `/${tournament.id}`, "_self")
    } catch (error) {
      console.error("Error finishing tournament:", error)
    }
  }

  const handleReportTournament = async () => {
    try {
      await tournamentFinish({ tournamentId: tournament.id })
      window.location.reload()
    } catch (error) {
      console.error("Error finishing tournament:", error)
    }
  }

  return (
    <Card className="mb-6 bg-white dark:bg-gray-800 dark:text-gray-100">
      <CardHeader className="p-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Título + Estado */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg md:text-xl dark:text-gray-100">
                Navegación del Torneo
              </CardTitle>
              <TournamentStatusBadge statusId={tournament?.statusId} extraText="Estado: " />
              {tournament.isPrivate && (
                <Badge variant="destructive">
                  <EyeOff className="h-4 h-4 inline " />
                  Torneo es Privado
                </Badge>
              )}
            </div>

            {!tournament.isPresential && (
              <Badge
                variant="destructive"
                className="flex items-center gap-1 w-fit dark:bg-red-700 dark:text-red-100"
              >
                <Wifi className="h-3 w-3" />
                Online
              </Badge>
            )}
          </div>

          {/* Botones de acciones activas del torneo */}
          <div className="flex flex-col w-full md:w-auto md:flex-row md:items-center gap-2 md:gap-2">
            {showEndTournament && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="default"
                    disabled={startLoading}
                    className="w-full md:w-auto shadow-md rounded-xl flex items-center justify-center gap-2 dark:bg-primary dark:text-gray-100"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {startLoading ? "Finalizando..." : "Finalizar Torneo"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="dark:bg-gray-700 dark:text-gray-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Finalizar Torneo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción finalizará el torneo "{tournamentName}". Una vez finalizado, no se
                      podrán hacer más cambios.
                      <br />
                      Al dar por terminado el torneo, no se realizarán más rondas, y los resultados
                      de la ronda actual serán los finales del evento.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleEndTournament}>
                      Sí, finalizar torneo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {showReportTournament && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="default"
                    disabled={finishLoading}
                    className="w-full md:w-auto shadow-md rounded-xl flex items-center justify-center gap-2 dark:bg-primary dark:text-gray-100"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {finishLoading ? "Reportando..." : "Reportar Torneo"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="dark:bg-gray-700 dark:text-gray-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Reportar Torneo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción reportará el torneo "{tournamentName}". Una vez finalizado, no se
                      podrán hacer más cambios.
                      <br />
                      Al reportar el torneo los resultados finales serán reflejados en el ranking de
                      la temporada.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReportTournament}>
                      Sí, reportar torneo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3">
        {/* Main Navigation */}
        <div
          className={`grid grid-cols-3 ${
            isAdmin || canManageTournament ? "md:grid-cols-5" : "md:grid-cols-3"
          } gap-4 mb-2`}
        >
          {!tournament.Store.active && isStoreOwner && (
            <div className="flex items-center justify-center">
              <div className="flex items-start gap-3 rounded-lg border border-red-400 bg-red-50 dark:bg-red-900 dark:border-red-600 px-4 py-3 max-w-md">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <h2 className="font-semibold text-red-800 dark:text-red-200">
                    Tienda desactivada
                  </h2>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Tu tienda no se encuentra disponible en este momento. Para reactivarla, por
                    favor contacta con el equipo de Juego Organizado
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-400 mt-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:jo@klu.cl" className="hover:underline">
                      jo@klu.cl
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Link to={tournament.urlView}>
            <Button
              variant={currentPage === "detail" ? "default" : "outline"}
              className="w-full h-20 flex-col shadow-md rounded-xl text-center break-words whitespace-normal "
            >
              <Info className="h-4 w-4" />
              Detalles Torneo
            </Button>
          </Link>

          {canManageTournament && (
            <Link to={tournament.urlAddPlayers}>
              <Button
                variant={currentPage === "players" ? "default" : "outline"}
                className="w-full h-20 flex-col shadow-md rounded-xl text-center break-words whitespace-normal"
              >
                <Users className="h-4 w-4" />
                Gestión Jugadores
              </Button>
            </Link>
          )}

          {stateRounds && (
            <Link to={tournament.urlRounds}>
              <Button
                variant={currentPage === "rounds" ? "default" : "outline"}
                className="w-full h-20 flex-col shadow-md rounded-xl text-center break-words whitespace-normal "
              >
                <Trophy className="h-4 w-4" />
                Rondas Resultados
              </Button>
            </Link>
          )}

          {(isAdmin || isStoreOwner) && (
            <Link to={`/tournament/report/${tournament.id}/report`}>
              <Button
                variant={currentPage === "report" ? "default" : "outline"}
                className="w-full h-20 flex-col shadow-md rounded-xl text-center break-words whitespace-normal "
              >
                <BarChart3 className="h-4 w-4" />
                Reporte Razas
              </Button>
            </Link>
          )}

          {(isAdmin || isStoreOwner) && (
            <Link to={`/tournament/admin/${tournament.id}`}>
              <Button
                variant={currentPage === "admin" ? "destructive" : "outline"}
                className="w-full h-20 flex-col shadow-md rounded-xl text-center break-words whitespace-normal dark:bg-red-700 dark:border-red-600 dark:text-gray-100"
              >
                <Cog className="h-4 w-4" />
                Gestión Admin
              </Button>
            </Link>
          )}
        </div>

        {/* Round Navigation */}
        {showRounds && rounds && rounds.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium mb-3 dark:text-gray-100">Navegación de Rondas</h4>
            <div
              className={`grid sm:grid-cols-2 ${rounds.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"} flex-wrap gap-2`}
            >
              {rounds.map((round) => {
                const isActive = round.id === Number(roundId)
                // En mobile solo priorizamos la ronda activa cuando se está
                // viendo el detalle de una ronda: ahí las rondas ya
                // terminadas (statusId 2) se ocultan, salvo que sea la que se
                // está viendo. Si se está viendo "Todas las rondas" (sin
                // roundId en la URL) o en desktop, se siguen mostrando todas.
                const isFinished = round.statusId === 2
                const hideOnMobile = !!roundId && isFinished && !isActive
                return (
                  <Card
                    key={round.id}
                    className={`w-full md:w-auto min-w-[200px] shadow-lg rounded-xl transition border-2 cursor-pointer
                      ${hideOnMobile ? "hidden sm:block" : ""}
                      ${
                        isActive
                          ? "border-primary bg-primary/10"
                          : "border hover:border-primary dark:hover:border-gray-400 dark:bg-gray-700 dark:text-gray-100"
                      }`}
                  >
                    <CardHeader className="pb-4">
                      <CardTitle className="text-center dark:text-gray-100">{round.name}</CardTitle>
                    </CardHeader>

                    <CardContent className="px-2 flex flex-col gap-2 py-4">
                      <div className="flex justify-center items-center">
                        <Badge
                          variant={round.statusId === 1 ? "secondary" : "default"}
                          className="text-xs mx-2 dark:bg-gray-600 dark:text-gray-100"
                        >
                          {roundStatus[round.statusId] || "N/A"}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="text-xs dark:border-gray-500 dark:text-gray-100"
                        >
                          Mesas: {round.totalMatches || 0}
                        </Badge>
                      </div>

                      <div className="flex flex-1 md:w-auto gap-2 cursor-pointer">
                        <Button
                          variant={pathRounds && isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => navigate(`${round.urlView}/${tournament.id}`)}
                          className="w-full md:w-auto dark:border-gray-600 dark:text-gray-100"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          Mesas
                        </Button>

                        {round.urlStanding && round.statusId === 2 && (
                          <Button
                            variant={pathStanding && isActive ? "default" : "outline"}
                            size="sm"
                            onClick={() => navigate(`${round.urlStanding}/${round.tournamentId}`)}
                            className="w-full md:w-auto dark:border-gray-600 dark:text-gray-100"
                          >
                            <Trophy className="h-3 w-3 mr-1" />
                            Posiciones
                          </Button>
                        )}
                      </div>

                      <div className="text-center text-xs text-muted-foreground dark:text-gray-300">
                        {new Date(round.created).toLocaleDateString("es-ES", {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 *
 * @param param0
 * @returns
 */
export const HeaderTournament = ({ tournament, round = null }) => {
  return (
    <div className="mb-0 pb-4">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Columna izquierda: imagen + subtítulo + badge */}
        <div className="flex flex-col items-center sm:items-start gap-2">
          <img
            src={tournament?.Game?.urlImage}
            alt={tournament?.Game?.name || ""}
            className="w-full sm:h-32 object-cover rounded-lg border dark:border-gray-600"
          />
        </div>

        {/* Columna derecha: título principal */}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            {round ? (
              <>
                <Trophy className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold dark:text-gray-100">{round.name}</h1>
                <Badge
                  variant={round.statusId === 1 ? "default" : "secondary"}
                  className="text-center inline dark:bg-gray-700 dark:text-gray-100"
                >
                  {roundStatus[round.statusId]}
                </Badge>
              </>
            ) : (
              <>
                <Store className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold dark:text-gray-100">{tournament.Store.name}</h1>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg text-muted-foreground dark:text-gray-300">{tournament?.name}</h2>
            {!tournament.isPresential && (
              <Badge
                variant="destructive"
                className="flex items-center gap-1 dark:bg-red-700 dark:text-gray-100"
              >
                <Wifi className="h-3 w-3" />
                Online
              </Badge>
            )}
          </div>

          <h4 className="text-sm text-muted-foreground dark:text-gray-400">
            {new Date(tournament.startDate).toLocaleDateString("es-ES", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </h4>
          <div
            className={`grid ${tournament.Season.isArchived === 1 ? "grid-cols-2" : "grid-cols-2"} gap-2 mt-2`}
          >
            <Badge
              variant={tournament.Season.isActive ? "secondary" : "destructive"}
              className="dark:border-gray-600 text-center"
            >
              Temporada {tournament.Season.isActive ? "Activa" : "Inactiva"}
            </Badge>
            {tournament.Season.isArchived === 1 && (
              <Badge variant={"outline"} className="dark:border-gray-600">
                <Archive className="w-3 h-3 mr-1" />
                Archivada
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
